import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, Upload, Settings, LogOut, History, Menu, X, User, ChevronLeft, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useUser } from '../../contexts/UserContext';
interface NavigationProps {
  onLogout?: () => void;
}
const navItems = [{
  name: 'Dashboard',
  path: '/app',
  icon: Home,
  exact: true
}, {
  name: 'Transcriptions',
  path: '/app/transcriptions',
  icon: FileText,
  exact: false
}, {
  name: 'Batch Process',
  path: '/app/batch',
  icon: Upload,
  exact: true
}, {
  name: 'Search',
  path: '/app/search',
  icon: Search,
  exact: false
}, {
  name: 'History',
  path: '/app/history',
  icon: History,
  exact: true
}, {
  name: 'Settings',
  path: '/app/settings',
  icon: Settings,
  exact: true
}];
export function Navigation({
  onLogout
}: NavigationProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    user,
    supabaseUser
  } = useUser();
  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };
  return <>
      {/* Desktop Navigation */}
      <nav className="w-60 h-[calc(100vh-2rem)] sticky top-4 mx-4 p-5 hidden md:flex md:flex-col bg-card/95 rounded-2xl shadow-lg ios-blur overflow-hidden transition-all duration-300 hover:shadow-xl" data-id="element-486">
        <div className="flex-shrink-0 mb-8 px-2" data-id="element-487">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-10 w-auto" data-id="element-488" />
        </div>
        {user && <div className="flex-shrink-0 mb-6 p-3 bg-primary/5 rounded-xl flex items-center" data-id="element-489">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3" data-id="element-490">
              <User size={14} className="text-primary" data-id="element-491" />
            </div>
            <div className="overflow-hidden" data-id="element-492">
              <p className="text-sm font-medium truncate" data-id="element-493">{user.email}</p>
              <div className="flex items-center" data-id="element-494">
                <p className="text-xs text-muted-foreground" data-id="element-495">
                  {user.plan.isPro ? 'Pro Plan' : 'Free Plan'}
                </p>
                {!user.plan.isPro && <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full" data-id="element-496">
                    {Math.max(0, 3 - user.usage.minutesUsed)} credits left
                  </span>}
              </div>
            </div>
          </div>}
        <div className="flex-grow overflow-y-auto min-h-0" data-id="element-497">
          <div className="space-y-1" data-id="element-498">
            {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname === item.path || item.path !== '/app' && location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={cn('flex items-center h-11 px-3 py-2 rounded-xl text-sm font-medium', 'transition-colors duration-200 group relative', isActive ? 'text-primary-foreground tiktok-gradient' : 'text-foreground/70 hover:text-foreground hover:bg-secondary')} data-id="element-499">
                  <Icon size={18} className={cn('mr-3', isActive ? 'text-primary-foreground' : 'text-foreground/50 group-hover:text-foreground/70')} data-id="element-500" />
                  <span data-id="element-501">{item.name}</span>
                </Link>;
          })}
          </div>
        </div>
        {onLogout && <div className="flex-shrink-0 mt-auto pt-4 border-t border-border/40" data-id="element-502">
            <button onClick={onLogout} className="flex w-full items-center h-11 px-3 py-2 rounded-xl text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors duration-200" data-id="element-503">
              <LogOut size={18} className="mr-3 text-foreground/50" data-id="element-504" />
              <span data-id="element-505">{supabaseUser ? 'Sign Out' : 'Exit Guest Mode'}</span>
            </button>
          </div>}
      </nav>

      {/* Mobile Navigation Header */}
      <div className="md:hidden sticky top-0 z-30 bg-card/80 ios-blur ios-shadow safe-top" data-id="element-506">
        <div className="flex items-center justify-between p-4 border-b border-border/20" data-id="element-507">
          <div className="flex items-center" data-id="element-508">
            {location.pathname !== '/app' && <Link to="/app" className="mr-3" data-id="element-509">
                <ChevronLeft size={24} className="text-foreground/70" data-id="element-510" />
              </Link>}
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-8 w-auto" data-id="element-511" />
          </div>
          <button onClick={toggleMobileMenu} className="p-2 rounded-full hover:bg-secondary text-foreground/70 hover:text-foreground transition-colors active:scale-95" aria-label="Toggle menu" data-id="element-512">
            {mobileMenuOpen ? <X size={24} data-id="element-513" /> : <Menu size={24} data-id="element-514" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity animate-fade-in" onClick={closeMobileMenu} data-id="element-515">
          <div className="fixed inset-y-0 left-0 w-[85%] max-w-xs bg-card/95 ios-blur ios-shadow-lg transform transition-transform duration-300 ease-in-out animate-slide-right overflow-y-auto safe-top safe-bottom" onClick={e => e.stopPropagation()} data-id="element-516">
            <div className="flex justify-between items-center p-5 border-b border-border/20" data-id="element-517">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-8 w-auto" data-id="element-518" />
              <button onClick={closeMobileMenu} className="p-2 rounded-full hover:bg-secondary text-foreground/70 hover:text-foreground transition-colors" aria-label="Close menu" data-id="element-519">
                <X size={20} data-id="element-520" />
              </button>
            </div>
            {user && <div className="mx-4 my-5 p-4 bg-primary/5 rounded-xl flex items-center" data-id="element-521">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mr-3" data-id="element-522">
                  <User size={16} className="text-primary" data-id="element-523" />
                </div>
                <div className="overflow-hidden" data-id="element-524">
                  <p className="font-medium truncate" data-id="element-525">{user.email}</p>
                  <p className="text-sm text-muted-foreground" data-id="element-526">
                    {user.plan.isPro ? 'Pro Plan' : 'Free Plan'}
                  </p>
                </div>
              </div>}
            <div className="p-4 space-y-2" data-id="element-527">
              {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname === item.path || item.path !== '/app' && location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={cn('flex items-center h-12 px-4 py-3 rounded-xl text-base font-medium', 'transition-colors duration-200 group relative', isActive ? 'text-primary-foreground tiktok-gradient' : 'text-foreground/70 hover:text-foreground hover:bg-secondary')} onClick={closeMobileMenu} data-id="element-528">
                    <Icon size={20} className={cn('mr-3', isActive ? 'text-primary-foreground' : 'text-foreground/50 group-hover:text-foreground/70')} data-id="element-529" />
                    <span data-id="element-530">{item.name}</span>
                  </Link>;
          })}
            </div>
            {onLogout && <div className="absolute bottom-8 left-4 right-4 pt-4 border-t border-border/20" data-id="element-531">
                <button onClick={() => {
            closeMobileMenu();
            onLogout();
          }} className="flex w-full items-center h-12 px-4 py-3 rounded-xl text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors duration-200" data-id="element-532">
                  <LogOut size={20} className="mr-3 text-foreground/50" data-id="element-533" />
                  <span data-id="element-534">{supabaseUser ? 'Sign Out' : 'Exit Guest Mode'}</span>
                </button>
              </div>}
          </div>
        </div>}
    </>;
}